import { type ReactNode, useState } from 'react';
import { practiceSettingsSubtitleSx } from '@features/organizations/components/PracticeSettings/practiceSettingsTypography';
import { SectionCardTitle } from '@features/organizations/components/SectionCardTitle';
import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';

import type { RequirementKind } from '../constants';
import type { DefinitionResponse, SubjectLanguage } from '../types';

import {
  type DefinitionPatch,
  RequirementDefinitionRow,
  WIDE_ONLY_CELL,
} from './RequirementDefinitionRow';

export interface PatientRequirementsCatalogViewProps {
  definitions: DefinitionResponse[];
  /**
   * Per-requirement pieces that need the store: the missing count, the last
   * bulk send, the practice message and the send flow. Render props so this
   * component stays presentational — every state is reachable from a story —
   * while its container decides what each piece is and who may have it.
   */
  renderMissingCell?: (definition: DefinitionResponse) => ReactNode;
  renderLastSendCell?: (definition: DefinitionResponse) => ReactNode;
  renderMessagePreview?: (definition: DefinitionResponse) => ReactNode;
  renderSendRequests?: (definition: DefinitionResponse) => ReactNode;
  renderLastSendDetail?: (definition: DefinitionResponse) => ReactNode;
  /** Guide control, inline after the page title (GDN-3703). */
  titleControl?: ReactNode;
  /** First load only — this is the one state that may hide the table. */
  isLoading: boolean;
  /**
   * A revalidation behind data already on screen. Shown as a hairline bar, and
   * deliberately NOT as a spinner over the table: every save invalidates the
   * list, so hiding the table here would unmount the open row mid-edit and
   * discard the save status the admin is watching.
   */
  isRefreshing?: boolean;
  isError: boolean;
  availableKinds: RequirementKind[];
  subject: SubjectLanguage;
  onUpdateDefinition: (definitionId: string, patch: DefinitionPatch) => Promise<void>;
  /** A row was opened. Closing is not reported. */
  onExpand?: (definition: DefinitionResponse) => void;
  onAddClick: () => void;
  onRetry: () => void;
  /** Open this row on first render; stories use it to show the detail. */
  initialExpandedId?: string;
}

/**
 * The requirement catalog as a table (GDN-4142; the catalog-table design the
 * human approved from the GDN-4123 round). It matches how Your Practice
 * already lists appointment types and templates: one row per requirement, a
 * row opens to edit it.
 *
 * One row open at a time. The detail is tall, and two open at once would put
 * one requirement's Send requests next to another's instructions.
 */
export const PatientRequirementsCatalogView = ({
  definitions,
  renderMissingCell,
  renderLastSendCell,
  renderMessagePreview,
  renderSendRequests,
  renderLastSendDetail,
  titleControl,
  isLoading,
  isRefreshing = false,
  isError,
  availableKinds,
  subject,
  onUpdateDefinition,
  onExpand,
  onAddClick,
  onRetry,
  initialExpandedId,
}: PatientRequirementsCatalogViewProps) => {
  const [expandedId, setExpandedId] = useState<string | null>(initialExpandedId ?? null);
  const title = `${subject.singularCap} requirements`;
  /*
    GDN-4141: this used to say the name is "Shown in texts, emails, and the
    patient's checklist". The request text and email are the practice-wide
    message and carry no requirement field (`request_copy_service.py`); the
    name appears only on the page the link opens.
  */
  const nameColumnHelp = `Shown on the page the request link opens. The text and email are the same for every requirement.`;
  const ready = !isLoading && !isError;

  return (
    <Box component="section" aria-label={title}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <SectionCardTitle title={title} control={titleControl} />
          <Typography variant="body2" sx={practiceSettingsSubtitleSx}>
            {`Choose what ${subject.plural} need to provide, and ask the ones who have not.`}
          </Typography>
        </Box>
        {availableKinds.length > 0 && ready && (
          <Button
            startIcon={<AddIcon />}
            onClick={onAddClick}
            variant="contained"
            sx={{ flexShrink: 0 }}
          >
            Add requirement
          </Button>
        )}
      </Box>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress aria-label="Loading requirements" />
        </Box>
      )}

      {!isLoading && isError && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={onRetry}>
              Retry
            </Button>
          }
        >
          We could not load the requirement catalog.
        </Alert>
      )}

      {ready && definitions.length === 0 && (
        <Box sx={{ py: 5, px: 3, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            No requirements yet.
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            Add a requirement to start collecting the information your practice needs.
          </Typography>
        </Box>
      )}

      {ready && definitions.length > 0 && (
        <>
          <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{ height: 2 }}>{isRefreshing && <LinearProgress sx={{ height: 2 }} />}</Box>
            <TableContainer
              tabIndex={0}
              role="region"
              aria-label="Requirement settings"
              sx={{
                '&:focus-visible': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: -2,
                },
              }}
            >
              <Table
                size="small"
                aria-label={title}
                sx={{
                  minWidth: { md: 760 },
                  '& th': { bgcolor: 'grey.50', fontWeight: 600, py: 1.25, whiteSpace: 'nowrap' },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>Requirement</TableCell>
                    {/*
                      The explanation lives in the column header, once, not
                      under every row (GDN-3365), as a tooltip so the header
                      stays one line (GDN-3537). The icon is focusable and
                      carries the text as its accessible name (`titleAccess`:
                      MUI hides an SvgIcon from the tree otherwise), so it is
                      reachable by keyboard and read by a screen reader.
                    */}
                    <TableCell sx={WIDE_ONLY_CELL}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {`Name ${subject.plural} see`}
                        <Tooltip title={nameColumnHelp}>
                          <InfoOutlinedIcon
                            fontSize="inherit"
                            tabIndex={0}
                            titleAccess={nameColumnHelp}
                            sx={{ color: 'text.secondary', cursor: 'help' }}
                          />
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell sx={WIDE_ONLY_CELL}>Auto-assign</TableCell>
                    <TableCell>Missing</TableCell>
                    <TableCell sx={WIDE_ONLY_CELL}>Last bulk send</TableCell>
                    <TableCell padding="checkbox">
                      <Box component="span" sx={visuallyHidden}>
                        Details
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {definitions.map(definition => {
                    const id = definition.patient_requirement_definition_id;
                    const expanded = expandedId === id;
                    return (
                      <RequirementDefinitionRow
                        key={id}
                        definition={definition}
                        subject={subject}
                        expanded={expanded}
                        onToggleExpanded={() => {
                          setExpandedId(expanded ? null : id);
                          if (!expanded) onExpand?.(definition);
                        }}
                        missingCell={renderMissingCell?.(definition)}
                        lastSendCell={renderLastSendCell?.(definition)}
                        messagePreview={renderMessagePreview?.(definition)}
                        sendRequests={renderSendRequests?.(definition)}
                        lastSendDetail={renderLastSendDetail?.(definition)}
                        onUpdate={patch => onUpdateDefinition(id, patch)}
                      />
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 1.5 }}>
            Open a requirement to edit it. Changes save when you leave a field.
          </Typography>
        </>
      )}
    </Box>
  );
};
